import { __decorate } from "tslib";
import { Vue, Component } from "vue-property-decorator";
import { Getter, Action } from "vuex-class";
// @ts-ignore
import { version as appVersion } from "../package.json";
import * as _ from "lodash";
import moment from "moment";
import { ConversationType, } from "@/helpers/types";
import { noticeCodes } from "./helpers/genesys";
import { _generateUUID, mainThread, debounce, getUrlQueryParams, WFP_ACCOMMODATION_THREADS, } from "./helpers/functions";
import strings from "./static/strings";
import { welcomeMessagePassengersPayload, welcomeMessageFocalPointPayload, restartMessagePayload, timeoutMessagePayload, autocompleteSingleMessageIncoming, autocompleteMultipleMessageIncoming, carouselMessageSingleChoiceImageText, } from "./static/mocks";
// now import WebChatCore module with customs overides
import WfpChat from "./helpers/webchat-overides";
import WebChatAuthenticator from "./authenticator/index";
import CallToAction from "@/components/CallToAction.vue";
Vue.use(WebChatAuthenticator, {
    context: "wfp",
    basePath: process.env.VUE_APP_API_ENDPOINT
});
let App = class App extends Vue {
    constructor() {
        super(...arguments);
        // XMPP domain
        this.DOMAIN = "wfp";
        // XMPP receiver
        this.RECEIVER = "va";
        // Back-end API version (needed from BE)
        this.API_VERSION = 5;
        this.ATTACHMENT_ENDPOINT = "";
        // LS_ means localStorage aliases
        this.LS_USER_COOKIE_CREDS_KEY = "_wcu";
        this.LS_XMPP_RESOURCE = "resource";
        this.LS_AUTO_CLEAN_LS_COUNTER = "_clean_ls_ctr";
        this.LS_GOOGLE_MAPS_API_LANG = "_gmaps_lang";
        this.LS_CURRENT_USER_THREAD = "_t";
        this.LS_MESSAGE_TREE = "_lmt_";
        this.LS_CONVERSATION_TREE = "_lct_";
        this.LS_CONVERSATION_COUNTER = "_lcc_";
        this.LS_LAST_MAM_MESSAGE_ID = "_last_mam_id_";
        this.LS_LAST_MESSAGE_TIMESTAMP = "_last_msg_ts_";
        this.fetchWfpConversationDaysLimit = 7; // refers to no. of days
        this.currentChatThread = "";
        this.showLoader = true;
        this.userCredentials = { EIMeVersion: 2 };
        this.showCloseChatCta = false;
        this.additionalURLParams = { useChatDynamic: true };
        this.chatHeaderTitle = "";
        this.showDialog = false;
        this.showChatBackButton = true;
        this.isChatConversationListVisited = false;
        // wfp confirm user actually click the privacy message, default:false
        this.isPrivacyMessageClick = false;
        this.sessionIntervalCycleCount = 0;
        // for dev testing
        // reduce to smaller number to test force xmpp logout(e.g. 20)
        this.sessionIntervalCyclesLimitSecs = 1800; // 30 min is 1800 sec
        this.chatMessageCount = 0; // is the total count of the exchange messages
        this.isTypingBubbleTimeoutSecs = 0; // timeout in secs
        this.showIsTypingBubbleSecs = 0; // no of sec before showing the typing bubble
        this.messageTimeoutSecs = 0; // timeout in secs
        this.shouldAllowLoginQueryParams = false; // flag to allow webchat to login with creds from query string
        this.xmppMessagePriority = 0; // xmpp message priority
        this.lastMessageToDisplayTotal = 50; // the last message to display count, spoc conversation only
        this.shouldAutoCleanLS = false; // flag to autoclean local storage
        this.autoCleanAttemptCount = 1; // the number of attempts to auto clean ls, default is 1
        this.googleMapsApiLang = "en"; // the indicated language use for google maps api, default is "en"
        this.showIsTypingBubble = false; // flag to show is typing
        this.isUserRelogin = false; // flag to identify new session
        // number limit to show the autocomplete field only if the single list choices is more than indicated
        this.singleChoiceLimitCount = 8;
        // number limit to show the autocomplete field only if the multiple list choices is more than indicated
        this.multipleChoiceLimitCount = 8;
        this.debugMode = false;
        // debounceShowTypingBubble: ((...args: any) => void) | undefined; // initiated inside created() hook
        // voice interface configs
        this.VOICE_INTERFACE_ENDPOINT = "";
        this.shouldEnableVoiceInterface = false; // flag to enable voice interface
        this.sttLanguage = "en-US"; // the indicated language for google stt transcription
    }
    /**
     * @returns {VueInstance} The instance of Webchat component reference.
     */
    get _webchatReference() {
        return this.$refs.webchat;
    }
    /**
     * @returns {VueInstance} The instance of Webchat component reference.
     */
    get _webchatConversationListReference() {
        return this._webchatReference.$refs.conversationList;
    }
    /**
     * @returns {string} Current decrypted "resource" from localStorage
     */
    get _resource() {
        return this._slsGet(this.LS_XMPP_RESOURCE);
    }
    get _getAutoCleanAttemptCounter() {
        return parseInt(this._slsGet(this.LS_AUTO_CLEAN_LS_COUNTER)) || 1;
    }
    get _getLastMamId() {
        return localStorage.getItem(this._getLocalLastMamMessageId) || "";
    }
    get _getLocalMessageTree() {
        return `${this.LS_MESSAGE_TREE}${this._resource}`;
    }
    get _getLocalConversationTree() {
        return `${this.LS_CONVERSATION_TREE}${this._resource}`;
    }
    get _getLocalConversationsCounter() {
        return `${this.LS_CONVERSATION_COUNTER}${this._resource}`;
    }
    get _getLocalLastMamMessageId() {
        return `${this.LS_LAST_MAM_MESSAGE_ID}${this._resource}`;
    }
    get _getLocalLastMessageTimestamp() {
        return `${this.LS_LAST_MESSAGE_TIMESTAMP}${this._resource}`;
    }
    get _getReceiver() {
        return `${this.RECEIVER}@${this.DOMAIN}`;
    }
    get _randomId() {
        return Math.random().toString(36).substr(2, 9);
    }
    get _getRenderedAsset() {
        return {
            avatar_wfp: this.renderAsset("wfp-icons/bot.svg"),
            avatar_operatore: this.renderAsset("wfp-icons/headphones.svg"),
            ico_user: this.renderAsset("wfp-icons/avatar.svg"),
            ico_wfp: this.renderAsset("wfp-icons/bot.svg"),
            ico_operatore: this.renderAsset("wfp-icons/headphones.svg")
        };
    }
    /**
     * @returns {Conversation} Default SPOC conversation object
     */
    get _wfpChat() {
        return {
            title: "SPOC",
            class: ConversationType.Wfp,
            avatar: this._getRenderedAsset.avatar_wfp,
            pin_date: Date.now() * 1000 // to be sure that Wfp's always on top of list
        };
    }
    /**
     * @returns {Array} Allowed set of parameters fetchable from URL
     */
    get _allowedUriParams() {
        return ["user", "token", "thread"];
    }
    /**
     * @returns {Object} Default "data" field to be passed into message payload
     */
    get _parsedURIParamsForMessagePayloadData() {
        return {
            ..._.omit(this.additionalURLParams, ["action", "thread"]),
            os: "web",
            apiVersion: this.API_VERSION,
            appVersion
        };
    }
    get _getDefaultChatConversationHeaderTitle() {
        return `<strong class="slogan">${this.chatHeaderTitle}</strong>`;
    }
    get _getChatConversationHeaderTitle() {
        const defaultTitle = [];
        defaultTitle.push(this._getDefaultChatConversationHeaderTitle);
        defaultTitle.push(`<span class="slogan-desc">${strings.defaultWfpHeaderPrivacy}</span>`);
        return defaultTitle.join("<br/>");
    }
    get _getTimeoutMessageMillisecond() {
        // 1 sec = 1000 millisecond
        return this.messageTimeoutSecs * 1000;
    }
    get _getShowIsTypingBubbleMillisecond() {
        // 1 sec = 1000 millisecond
        return this.showIsTypingBubbleSecs * 1000;
    }
    get getLabel() {
        return {
            heading_conversation_list: strings.defaultConversationListHeading,
            heading_cases_history: strings.defaultCasesHistoryHeading,
            label_back_to_conversation_list: strings.defaultBackToConversationListLabel,
            label_ask_bot: strings.defaultAskBotLabel,
            label_xmpp_connection_message: strings.defaultXmppConnectionMessage,
            label_select_button_first: strings.defaultSelectButtonLabel,
            label_input_text_placeholder: strings.defaultInputTextPlaceholder,
            label_input_date_placeholder: strings.defaultInputDatePlaceholder,
            label_input_location_placeholder: strings.defaultInputLocationPlaceholder,
            label_popup_location_placeholder: strings.defaultPopupLocationPlaceholder,
            label_input_autocomplete_placeholder: strings.defaultInputAutocompletePlaceholder,
            label_popup_button_cancel: strings.defaultPopupButtonCancel,
            label_popup_button_confirm: strings.defaultPopupButtonConfirm,
        };
    }
    get getAssets() {
        return {
            // new_question|restart
            icon_restart: "new_question",
        };
    }
    get getBotConfigSettings() {
        return {
            labels: { ...this.getLabel },
            assets: { ...this.getAssets },
            googleMapsApiLangKey: this.LS_GOOGLE_MAPS_API_LANG,
            singleChoiceLimitCount: this.singleChoiceLimitCount,
            multipleChoiceLimitCount: this.multipleChoiceLimitCount,
        };
    }
    _slsGet(key, type = "") {
        return this._webchatReference.slsGet(key, type);
    }
    _slsSet(key, value) {
        this._webchatReference.slsSet(key, value);
    }
    created() {
        // setup xmpp env overrides
        this.setupXmppEnvConfig();
        this.$on("CLOSE_CHAT_DIALOG", (status) => (this.showDialog = status));
        this.parseURLParams();
        // with vue2, its important to put debounce inside created() hook and not on methods
        // because the component is using export default { ... },
        // including the methods are going to be reused by all the instances of the component
        // which means there could be duplicates of the same debounce that we want to prevent
        this.debounceTimeoutMessage = debounce((msg, lastMsgTimestamp) => {
            this.handleLastConversationTimeoutMessage(msg, lastMsgTimestamp);
        }, this._getTimeoutMessageMillisecond);
        // for archives
        this.debounceArchiveConversationMessage = debounce(() => {
            // handle welcome message
            this.handleWelcomeMessage();
            // handle input modes before/after modes
            this.handleInputModesBeforeAfter();
        }, 1000);
        // scrolldown conversation 
        this.debounceScrollDownConversation = debounce((keyThread, mode, msg) => {
            // reset unread messages
            this._webchatReference.handleFireResetUnreadMessages(keyThread, mode, msg);
            // handle scrolldown
            this._webchatReference.onScrollDownConversation();
            console.log("auto scrolling down...");
        }, 500);
        // handle voice interface 
        this.debounceHandleVoiceInterface = debounce((msg) => {
            // handle scrolldown
            this.handleFireVoiceInterface();
        }, 500);
        // handle show is typing bubble
        /* this.debounceShowTypingBubble = debounce((toggle) => {
          // toggle show typing bubble
          this.handleShowIsTypingBubble(toggle);
        }, this._getShowIsTypingBubbleMillisecond); */
    }
    beforeDestroy() {
        this.$off("CLOSE_CHAT_DIALOG");
    }
    mounted() {
        console.log(`v${appVersion}`);
        // handle auto clean ls
        this.handleAutoCleanLocalStorages();
        // handle fetching login creds
        this.handleFetchXmppCredsFromExternal((params) => {
            // handle before login processes
            this.handleChecksBeforeLogin(params)
                .then(() => {
                // login
                this.loginAsUser();
                // clear all session first
                this.handleClearSessionInspector();
                // activate the session inspector
                this.setupConnectionSessionInspector();
                // setup conversation tree
                this.setupConversationTree();
                // handle chat display
                this.renderConversationsOrShowDedicatedChat();
                // @ts-ignore
                this.showLoader = false;
                this.initAttachmentsUploader();
            })
                .catch((err) => {
                // @ts-ignore
                this.showLoader = false;
                console.error(err);
            });
        });
    }
    isThreadPassengers() {
        return this.getMainThread() === WFP_ACCOMMODATION_THREADS.passengers;
    }
    isThreadFocalPoint() {
        return this.getMainThread() === WFP_ACCOMMODATION_THREADS.focal_point;
    }
    getMainThread() {
        return mainThread(this.additionalURLParams.thread);
    }
    getLocalUserCreds() {
        const userCreds = this._slsGet(this.LS_USER_COOKIE_CREDS_KEY);
        // if userCreds is available from localStorage
        // returns an encoded string
        // otherwise returns empty object
        return typeof userCreds === "string" && userCreds !== "" ? JSON.parse(atob(userCreds)) : { user: "", password: "" };
    }
    isLocalUserCredsExists() {
        const { user, password } = this.getLocalUserCreds();
        return user !== "" && password !== "";
    }
    isLocalUserCredsTheSame(xmppCreds) {
        if (!this.isLocalUserCredsExists()) {
            return false;
        }
        const { user } = xmppCreds;
        const { user: userLocal } = this.getLocalUserCreds();
        return userLocal === user;
    }
    handleAutoCleanLocalStorages() {
        let autoCleanAttemptCounter = this._getAutoCleanAttemptCounter;
        if (this.shouldAutoCleanLS && autoCleanAttemptCounter <= this.autoCleanAttemptCount) {
            console.log("attempting to auto clean local storage, attempt count is ", autoCleanAttemptCounter);
            // reset all existing local storage
            localStorage.clear();
        }
    }
    handleUpdateAutoCleanAttemptCounter() {
        let autoCleanAttemptCounter = this._getAutoCleanAttemptCounter;
        // increment until autoclean count
        if (this._getAutoCleanAttemptCounter <= this.autoCleanAttemptCount) {
            autoCleanAttemptCounter++;
        }
        // ensure the clean attempt is updated
        this._slsSet(this.LS_AUTO_CLEAN_LS_COUNTER, autoCleanAttemptCounter);
    }
    handleChecksBeforeLogin(params) {
        return new Promise((resolve, reject) => {
            const { user, token, thread } = params;
            if (user && token && thread) {
                let userCreds = { user, password: token };
                this.setXmppCreds({ ...params, user: encodeURIComponent(user) });
                // TODO: leave this as is for now,
                // additionalURLParams is important
                // as its so tied with business logic
                // we cant just simple remove them
                // without proper testing afterwards
                this.additionalURLParams = {
                    ...this.additionalURLParams,
                    token,
                    thread
                };
                setTimeout(() => {
                    // if user is not the same
                    if (!this.isLocalUserCredsTheSame(params)) {
                        // reset all existing local storage
                        // solves everything, without selective reset
                        localStorage.clear();
                    }
                    // then, update user and thread
                    // update the token with the same user
                    this._slsSet(this.LS_USER_COOKIE_CREDS_KEY, btoa(JSON.stringify(userCreds)));
                    // update thread
                    this._slsSet(this.LS_CURRENT_USER_THREAD, thread);
                    // update google maps api language
                    this._slsSet(this.LS_GOOGLE_MAPS_API_LANG, this.googleMapsApiLang);
                    // update auto clean attempt counter
                    this.handleUpdateAutoCleanAttemptCounter();
                    resolve("Before login operations successful.");
                }, 400);
            }
            else {
                reject("Missing parameters before login operation.");
            }
        });
    }
    handleAdjustedConversationWithoutTimeoutMessage(mode = "continue") {
        // we need to adjust the current conversation message
        // for spoc conversation
        const localMessagesTree = this.getLocalMessagesDictionary();
        const mainThreadLocal = this.getMainThread();
        if (localMessagesTree.hasOwnProperty(mainThreadLocal)) {
            // only continue, if localStorage has spoc conversation
            const conversationMessages = [...localMessagesTree[mainThreadLocal]];
            if (conversationMessages.length > 0) {
                if (mode === "continue") {
                    // if mode is continue
                    // first, remove the timeout message
                    // w/c is the last item of the current conversation
                    conversationMessages.pop();
                    // 2nd, now again, remove another last item
                    // but this time instead, record this last message item
                    // why? because we need to re-insert this last item again
                    // but we have to go to so many interlink processes
                    // (i.e. recording history, the timestamp, enable/disable the input, etc)
                    // that it only make sense to re-insert it again
                    // by just calling the already existing storeMessage(msg)
                    // instead of handling all those processes here
                    const lastMsgItem = conversationMessages.pop();
                    // reassign spoc thread with corrected conversation
                    localMessagesTree[mainThreadLocal] = [...conversationMessages];
                    // reset the current conversation with the already removed
                    // last 2 items(timeoutMessage, actualMessage) from the current conversation
                    this._webchatReference.setCurrentConversationMessages([...conversationMessages]);
                    // also reset the localMessageTree
                    this._slsSet(this._getLocalMessageTree, localMessagesTree);
                    // now finally, re-insert the actual last message item
                    console.log("refiring last message before timeout: ", lastMsgItem);
                    this._webchatReference.storeMessage(lastMsgItem);
                }
                else {
                    // otherwise, if mode is restart
                    // first get the last message item
                    const lastMsgItem = conversationMessages[conversationMessages.length - 1];
                    if (this._webchatReference.isMessageTimeoutNode(lastMsgItem)) {
                        // if the last message item is a timeout message
                        // then, just remove the timeout message
                        // w/c is the last item of the current conversation
                        conversationMessages.pop();
                        // reassign spoc thread with corrected conversation
                        localMessagesTree[mainThreadLocal] = [...conversationMessages];
                        // reset the current conversation with the already removed
                        // timeoutMessage from the current conversation
                        this._webchatReference.setCurrentConversationMessages([...conversationMessages]);
                        // also reset the localMessageTree
                        this._slsSet(this._getLocalMessageTree, localMessagesTree);
                    }
                }
            }
        }
    }
    handleAdjustSpocConversationMessage() {
        // we need to adjust conversation messages from spoc
        // to only the last 10 messages(atleast for now)
        // but its configurable at build time from here lastMessageToDisplayTotal
        const localMessagesTree = this.getLocalMessagesDictionary();
        const mainThreadLocal = this.getMainThread();
        // if localStorage has spoc conversation,
        // we need to adjust the number of conversation messages
        // to last message to display count
        if (localMessagesTree.hasOwnProperty(mainThreadLocal)) {
            if (localMessagesTree[mainThreadLocal].length > this.lastMessageToDisplayTotal) {
                const lastConversationMessages = localMessagesTree[mainThreadLocal].slice(-this.lastMessageToDisplayTotal);
                localMessagesTree[mainThreadLocal] = [...lastConversationMessages];
                // realign localMessageTree
                this._slsSet(this._getLocalMessageTree, localMessagesTree);
            }
        }
    }
    getPreviousDateByDays(daysLimit = 0, format = "iso") {
        const today = new Date();
        today.setDate(today.getDate() - daysLimit);
        if (format === "iso") {
            return new Date(today).toISOString();
        }
        else {
            return new Date(today).toString();
        }
    }
    getCorrectedTimestamp(noOfDays = 0) {
        const daysLimit = noOfDays || this.fetchWfpConversationDaysLimit;
        const lastMsgTimestamp = localStorage.getItem(this._getLocalLastMessageTimestamp);
        const lastWeekTimestamp = this.getPreviousDateByDays(daysLimit);
        // lets default timestamp from a week old
        // as requested change 2/01/2024
        // for wfp conversations
        let correctedTimestamp = lastWeekTimestamp;
        if (lastMsgTimestamp !== null) {
            const lastMsgTimestampMilli = new Date(lastMsgTimestamp).getTime();
            const lastWeekTimestampMilli = new Date(lastWeekTimestamp).getTime();
            // if lastMsgTimestamp is greater than a week ago, ok
            if (lastMsgTimestampMilli > lastWeekTimestampMilli) {
                // update localStorage with timestamp from last message
                correctedTimestamp = new Date(lastMsgTimestamp).toISOString();
            }
        }
        return correctedTimestamp;
    }
    getSpocWelcomeMessage() {
        let payload = welcomeMessagePassengersPayload;
        if (this.isThreadFocalPoint()) {
            payload = welcomeMessageFocalPointPayload;
        }
        return this.getSetupXMPPMessageEnvelope({
            uuid: _generateUUID(),
            thread: this.getMainThread(),
            // html_message: msgContent,
            raw_payload: { ...payload },
        });
    }
    getSetupXMPPMessageEnvelope(args = {}) {
        const { html_message, raw_payload, subject, type, action, uuid, direction, thread } = args;
        const simplePayload = {
            content: [
                {
                    type: "chat_html",
                    html: html_message,
                    is_notice: false
                }
            ]
        };
        let envelope = {
            subject: subject || "dynamic",
            type: type || "chat_dynamic",
            action: action || "chat_dynamic",
            stanza: {
                id: uuid,
                direction: direction || "in",
                thread,
                timestamp: moment().format()
            },
            payload: {},
            avatar: this._getRenderedAsset.avatar_wfp,
        };
        if (raw_payload) {
            envelope = {
                ...envelope,
                payload: raw_payload
            };
        }
        else {
            envelope = {
                ...envelope,
                payload: simplePayload
            };
        }
        return envelope;
    }
    checkChatQuickActionsFromURI() {
        const URIActions = this.additionalURLParams?.action?.split(",");
        if (URIActions && URIActions.length) {
            URIActions.forEach((str) => {
                // @ts-ignore
                const match = this.$store.state.quickActionsMap.find((action) => action.key === str);
                if (match) {
                    const conversationWhereToAct = this.getLocalConversationsArray().find((conversation) => conversation.class === match.conversation);
                    const silentMessage = this.getSetupXMPPMessageEnvelope({
                        uuid: _generateUUID(),
                        direction: "out",
                        thread: conversationWhereToAct.thread || this.getMainThread(),
                        html_message: match.message
                    });
                    this._webchatReference.sendMessageAction(silentMessage);
                }
            });
        }
    }
    setupXmppEnvConfig() {
        // override XMPP endpoint if present in config file
        if (process.env.VUE_APP_XMPP_ENDPOINT) {
            this.userCredentials = {
                ...this.userCredentials,
                host: process.env.VUE_APP_XMPP_ENDPOINT
            };
        }
        // override XMPP domain if present in config file
        if (process.env.VUE_APP_XMPP_DOMAIN) {
            this.DOMAIN = process.env.VUE_APP_XMPP_DOMAIN;
        }
        // override XMPP receiver if present in config file
        if (process.env.VUE_APP_XMPP_RECEIVER) {
            this.RECEIVER = process.env.VUE_APP_XMPP_RECEIVER;
        }
        // setup isTypingBubbleTimeoutSecs
        if (process.env.VUE_APP_IS_TYPING_TIMEOUT_SECS) {
            this.isTypingBubbleTimeoutSecs = process.env.VUE_APP_IS_TYPING_TIMEOUT_SECS;
        }
        // setup showIsTypingBubbleSecs
        if (process.env.VUE_APP_SHOW_IS_TYPING_BUBBLE_SECS) {
            this.showIsTypingBubbleSecs = process.env.VUE_APP_SHOW_IS_TYPING_BUBBLE_SECS;
        }
        // setup messageTimeoutSecs
        if (process.env.VUE_APP_MESSAGE_TIMEOUT_SECS) {
            this.messageTimeoutSecs = process.env.VUE_APP_MESSAGE_TIMEOUT_SECS;
        }
        // setup shouldAllowLoginQueryParams
        if (process.env.VUE_APP_SHOULD_ALLOW_LOGIN_QUERY_PARAMS) {
            this.shouldAllowLoginQueryParams = process.env.VUE_APP_SHOULD_ALLOW_LOGIN_QUERY_PARAMS.toLocaleLowerCase() === "true";
        }
        // setup VUE_APP_XMPP_MESSAGE_PRIORITY
        if (process.env.VUE_APP_XMPP_MESSAGE_PRIORITY) {
            this.xmppMessagePriority = parseInt(process.env.VUE_APP_XMPP_MESSAGE_PRIORITY);
        }
        // setup VUE_APP_LAST_MESSAGE_TO_DISPLAY_TOTAL
        if (process.env.VUE_APP_LAST_MESSAGE_TO_DISPLAY_TOTAL) {
            this.lastMessageToDisplayTotal = parseInt(process.env.VUE_APP_LAST_MESSAGE_TO_DISPLAY_TOTAL);
        }
        // setup VUE_APP_SHOULD_AUTO_CLEAN_LS
        if (process.env.VUE_APP_SHOULD_AUTO_CLEAN_LS) {
            this.shouldAutoCleanLS = process.env.VUE_APP_SHOULD_AUTO_CLEAN_LS.toLocaleLowerCase() === "true";
        }
        // setup VUE_APP_AUTO_CLEAN_ATTEMPT_COUNT
        if (process.env.VUE_APP_AUTO_CLEAN_ATTEMPT_COUNT) {
            this.autoCleanAttemptCount = parseInt(process.env.VUE_APP_AUTO_CLEAN_ATTEMPT_COUNT);
        }
        // setup VUE_APP_GOOGLE_MAPS_API_LANG
        if (process.env.VUE_APP_GOOGLE_MAPS_API_LANG) {
            this.googleMapsApiLang = process.env.VUE_APP_GOOGLE_MAPS_API_LANG;
        }
        // setup VUE_APP_SINGLE_CHOICES_LIMIT_COUNT
        if (process.env.VUE_APP_SINGLE_CHOICES_LIMIT_COUNT) {
            this.singleChoiceLimitCount = parseInt(process.env.VUE_APP_SINGLE_CHOICES_LIMIT_COUNT);
        }
        // setup VUE_APP_MULTIPLE_CHOICES_LIMIT_COUNT
        if (process.env.VUE_APP_MULTIPLE_CHOICES_LIMIT_COUNT) {
            this.multipleChoiceLimitCount = parseInt(process.env.VUE_APP_MULTIPLE_CHOICES_LIMIT_COUNT);
        }
        // setup debugMode
        if (process.env.VUE_APP_DEBUG) {
            this.debugMode = process.env.VUE_APP_DEBUG;
        }
        // setup voice interface endpoint
        if (process.env.VUE_APP_VOICE_INTERFACE_ENDPOINT) {
            this.VOICE_INTERFACE_ENDPOINT = process.env.VUE_APP_VOICE_INTERFACE_ENDPOINT;
        }
        // setup VUE_APP_SHOULD_ENABLE_VOICE_INTERFACE
        if (process.env.VUE_APP_SHOULD_ENABLE_VOICE_INTERFACE) {
            this.shouldEnableVoiceInterface = process.env.VUE_APP_SHOULD_ENABLE_VOICE_INTERFACE.toLocaleLowerCase() === "true";
        }
        // setup language use for google stt api
        if (process.env.VUE_APP_STT_API_LANG) {
            this.sttLanguage = process.env.VUE_APP_STT_API_LANG;
        }
    }
    setupConversationTree() {
        const localConversations = this.getLocalConversationsArray();
        const wfpChatReferenceIndex = localConversations.findIndex((conv) => conv.class === ConversationType.Wfp);
        // if Wfp's chat
        if (wfpChatReferenceIndex === -1) {
            localConversations.push(this._wfpChat);
        }
        this._webchatReference.setChatAvatarIcon(this._getRenderedAsset.avatar_wfp);
        // realign local storage
        this._slsSet(this._getLocalConversationTree, localConversations);
        // handle adjust spoc conversation messages
        this.handleAdjustSpocConversationMessage();
    }
    initAttachmentsUploader() {
        if (process.env.VUE_APP_ATTACHMENT_ENDPOINT) {
            this._webchatReference.setAttachmentsUploaderConfig({
                ...this._webchatReference.attachmentsUploaderConfig,
                actionPath: process.env.VUE_APP_ATTACHMENT_ENDPOINT
            });
        }
    }
    renderConversationsOrShowDedicatedChat() {
        const localConversations = this.getLocalConversationsArray();
        for (const conversation of localConversations) {
            const { class: currentClass } = conversation;
            if (currentClass && currentClass !== ConversationType.Deleted) {
                this._webchatReference.pushConversation(conversation);
            }
        }
        const matchIndex = localConversations
            .findIndex(({ class: currentClass }) => currentClass === ConversationType.Active);
        if (localConversations.length === 1) {
            const index = matchIndex !== -1 ? matchIndex : 0;
            this.setupConversationUI(localConversations[index]);
            this._webchatReference.setCurrentChat(localConversations[index]);
        }
        else {
            setTimeout(() => this._webchatReference.setCurrentChat({}), 3);
        }
        this._slsSet(this._getLocalConversationTree, localConversations);
        setTimeout(() => {
            this._webchatReference.$refs.conversationList.handleFetchHistoryMessages();
        }, 100);
    }
    setupConversationUI(conversation) {
        // update UI elements
        if (conversation.class !== ConversationType.Wfp) {
            // console.log('setupConversationUI', 'NOT SPOC MODE');
            this.chatHeaderTitle = conversation.title || strings.defaultChatTitle; // operator
            this._webchatReference.setChatHeaderTitle(this.chatHeaderTitle);
            this._webchatReference.setIsConversationHeaderRawHtml(false);
        }
        else {
            this.chatHeaderTitle = this.isThreadFocalPoint() ?
                strings.defaultWfpTitleFocalpoints : strings.defaultWfpTitlePassengers;
            // commented, bcos we disable privacy on the header
            // this._webchatReference.setChatHeaderTitle(this._getChatConversationHeaderTitle);
            // this._webchatReference.setIsConversationHeaderRawHtml(true);
            this._webchatReference.setChatHeaderTitle(this.chatHeaderTitle);
            this._webchatReference.setIsConversationHeaderRawHtml(false);
        }
        const chatBotMainIcon = conversation.class === ConversationType.Wfp
            ? this._getRenderedAsset.ico_wfp
            : this._getRenderedAsset.avatar_operatore;
        this._webchatReference.setChatHeaderIcon(chatBotMainIcon);
        // TODO: check on this
        this._webchatReference.toggleUserInputArea(false);
    }
    setupConnectionSessionInspector(waitFor = 1000) {
        this.sessionIntervalCycleCount = 0;
        this.sessionInterval = setInterval(() => {
            if (this.sessionIntervalCycleCount < this.sessionIntervalCyclesLimitSecs) {
                this.sessionIntervalCycleCount++;
            }
            else {
                this.logout();
                console.log(`Reached maximum allowed time is ${this.sessionIntervalCyclesLimitSecs}sec for this session. I'm logging out...`);
            }
        }, waitFor);
    }
    handleClearSessionInspector() {
        // clear webchatcore default session
        clearInterval(this._webchatReference.sessionInterval);
        this._webchatReference.sessionInterval = null;
        // since we only extend modification for webchatcore functionality
        // we need to clear our own session
        clearInterval(this.sessionInterval);
        this.sessionInterval = null;
    }
    handleFetchXmppCredsFromExternal(callback = (params) => { }) {
        if (this.shouldAllowLoginQueryParams) {
            // fetch creds from query string
            const { user, token, thread } = this.additionalURLParams;
            if (user && token) {
                const creds = {
                    // user: `${user.split("@")[0]}.${token}`,
                    user,
                    token,
                    thread: thread || this.getMainThread(),
                };
                // handle callback
                console.log("DEBUG: login parameters from query", { ...creds });
                callback({ ...creds });
            }
            else {
                console.warn("Missing parameters from query.");
            }
        }
        else {
            // fetch from postMessage
            window.addEventListener("message", (event) => {
                const safeSrcs = process.env.VUE_APP_ORIGIN_SAFE_SRCS;
                let safeSrcsArr = [];
                if (safeSrcs) {
                    safeSrcsArr = safeSrcs.toString().split(",");
                }
                // verify domain
                if (!safeSrcsArr.includes(event.origin)) {
                    console.warn("Message received from unauthorized source:");
                    return;
                }
                // Leggi i dati dal messaggio
                const data = event.data;
                if (data) {
                    // TODO: check on this, why with the fallback seems
                    // to add multiple open xmpp connections
                    const { user, token, thread } = data;
                    if (user && token) {
                        const creds = {
                            user,
                            token,
                            thread: thread || this.getMainThread(),
                        };
                        // handle callback
                        console.log("DEBUG: login parameters from postMessage", { ...creds });
                        callback({ ...creds });
                    }
                }
                else {
                    console.error("Missing parameters in the received message");
                }
            });
        }
    }
    getXmppLoginCreds() {
        const { user, token, thread } = this.xmppCreds;
        return {
            user,
            password: token,
            thread
        };
    }
    logout() {
        this._webchatReference.XMPPLogout();
        this.handleClearSessionInspector();
    }
    reLogin() {
        this.showLoader = true;
        this.loginAsUser();
        // clear all session first
        this.handleClearSessionInspector();
        // activate the session inspector
        this.setupConnectionSessionInspector();
        // clear loader
        this.showLoader = false;
    }
    loginAsUser() {
        const { user, password } = this.getXmppLoginCreds();
        this.userCredentials = {
            ...this.userCredentials,
            priority: this.xmppMessagePriority,
            user,
            password
        };
        try {
            console.log("DEBUG: XMPPLogin method call", this.userCredentials);
            this._webchatReference.XMPPLogin(this.userCredentials);
        }
        catch (error) {
            this._webchatReference.handleXmppConnectionError(error);
        }
    }
    parseURLParams() {
        const uriKeysMap = { ver: "channelVersion" };
        const queryParams = getUrlQueryParams();
        for (const [key, value] of Object.entries(queryParams)) {
            if (this._allowedUriParams.includes(key)) {
                const payloadKey = uriKeysMap[key] || key;
                const urlParams = { ...this.additionalURLParams };
                this.additionalURLParams = {
                    ...urlParams,
                    [payloadKey]: value
                };
            }
        }
    }
    toggleIsChatConversationListVisited(toggle = false) {
        this.isChatConversationListVisited = toggle;
    }
    toggleIsPrivacyMessageClick(toggle = false) {
        this.isPrivacyMessageClick = toggle;
    }
    handleInputModesBeforeAfter(msg = {}) {
        let msgItem = msg;
        if (Object.keys(msg).length <= 0) {
            // get the last item of the getCurrentChatMessages
            const [lastMsgItem] = this._webchatReference.getCurrentChatMessages.slice(-1);
            msgItem = lastMsgItem || {};
        }
        const { payload } = msgItem;
        if (payload) {
            const { inputData } = payload;
            if (inputData && inputData.choice) {
                const { modeBeforeSubmit, visibilityAfterSubmit } = inputData.choice;
                // console.log("modeBeforeSubmit: ", modeBeforeSubmit);
                // console.log("visibilityAfterSubmit: ", visibilityAfterSubmit);
                this._webchatReference.setModeBeforeSubmit(modeBeforeSubmit);
                this._webchatReference.setVisibilityAfterSubmit(visibilityAfterSubmit);
            }
            else {
                // reset
                this._webchatReference.setModeBeforeSubmit("");
                this._webchatReference.setVisibilityAfterSubmit("");
            }
        }
    }
    handleWelcomeMessage(force = false, storeInLs = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && currentChatMessages.length <= 0) || force) {
            const welcomeMessage = this.getSpocWelcomeMessage();
            this._webchatReference.storeMessage(welcomeMessage, storeInLs);
        }
    }
    handleTimeoutMessage(force = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && !currentChatMessages.length) || force) {
            const timeoutMessage = this.getSetupXMPPMessageEnvelope({
                uuid: _generateUUID(),
                thread: this.getMainThread(),
                is_premium_message: false,
                raw_payload: { ...timeoutMessagePayload },
                subject: "text",
                type: "chat_text",
                action: "chat_text",
                direction: "in",
            });
            this._webchatReference.storeMessage(timeoutMessage, false);
        }
    }
    handleRestartMessage() {
        const restartChatMessage = this.getSetupXMPPMessageEnvelope({
            uuid: _generateUUID(),
            thread: this.getMainThread(),
            is_premium_message: false,
            raw_payload: { ...restartMessagePayload },
            subject: "text",
            type: "chat_text",
            action: "chat_text",
            direction: "out"
        });
        this._webchatReference.sendMessageAction(restartChatMessage);
        // reset location
        this._webchatReference.setIsInteractionInsertLocation(false);
        // reset date node
        this._webchatReference.setIsInteractionInsertDate(false);
        this._webchatReference.setCurrentDatePickerInfo({});
        // reset autocomplete
        this._webchatReference.setIsInteractionSingleAutocomplete(false);
        this._webchatReference.setIsInteractionMultipleAutocomplete(false);
        this._webchatReference.setCurrentAutocompleteInfo({});
        this._webchatReference.setCurrentAutocompleteList([]);
    }
    handleSpeechToTextMessage(message) {
        const sttChatMessage = this.getSetupXMPPMessageEnvelope({
            uuid: _generateUUID(),
            thread: this.getMainThread(),
            is_premium_message: false,
            raw_payload: { text: message },
            subject: "text",
            type: "chat_text",
            action: "chat_text",
            direction: "out"
        });
        this._webchatReference.sendMessageAction(sttChatMessage);
    }
    handleLastConversationTimeoutMessage(msg, lastMsgTimestamp) {
        console.log("timeout lastMsgTimestamp: ", lastMsgTimestamp);
        // trigger timeout message to all nodes
        // except if a node is a restart node
        // or a node is a final node
        const isMessageRestartNode = this._webchatReference.isMessageRestartNode(msg);
        const isMessageFinalNode = this._webchatReference.isMessageFinalNode(msg);
        console.log("isRestartNode: ", isMessageRestartNode);
        console.log("isFinalNode: ", isMessageFinalNode);
        if (!(isMessageRestartNode || isMessageFinalNode)) {
            this.handleTimeoutMessage(true);
            // reset location node, if present
            this._webchatReference.setIsInteractionInsertLocation(false);
            this._webchatReference.setIsLocationModalEnable(false);
            // reset date node, if present
            this._webchatReference.setIsInteractionInsertDate(false);
            this._webchatReference.setCurrentDatePickerInfo({});
            // TODO: are there other possible resets too? find out...
        }
    }
    // TODO: remove when autocomplete single message incoming is working
    handleAutocompleteSingleMessage(force = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && !currentChatMessages.length) || force) {
            const autocompleteChatMessage = this.getSetupXMPPMessageEnvelope({
                uuid: _generateUUID(),
                thread: this.getMainThread(),
                is_premium_message: false,
                raw_payload: { ...autocompleteSingleMessageIncoming },
                subject: "text",
                type: "chat_text",
                action: "chat_text",
                direction: "in",
            });
            this._webchatReference.storeMessage(autocompleteChatMessage, false);
        }
    }
    // TODO: remove when autocomplete multiple message incoming is working
    handleAutocompleteMultipleMessage(force = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && !currentChatMessages.length) || force) {
            const autocompleteChatMessage = this.getSetupXMPPMessageEnvelope({
                uuid: _generateUUID(),
                thread: this.getMainThread(),
                is_premium_message: false,
                raw_payload: { ...autocompleteMultipleMessageIncoming },
                subject: "text",
                type: "chat_text",
                action: "chat_text",
                direction: "in",
            });
            this._webchatReference.storeMessage(autocompleteChatMessage, false);
        }
    }
    // TODO: remove when carousel message incoming is working
    handleCarouselMessage(force = false) {
        const currentChatMessages = this._webchatReference.getCurrentChatMessages;
        const isConversationsList = this._webchatReference.canShowConversationsView;
        if ((!isConversationsList && !currentChatMessages.length) || force) {
            const carouselMessage = this.getSetupXMPPMessageEnvelope({
                uuid: _generateUUID(),
                thread: this.getMainThread(),
                raw_payload: { ...carouselMessageSingleChoiceImageText },
                is_premium_message: false
            });
            this._webchatReference.storeMessage(carouselMessage, false);
        }
    }
    handleFireVoiceInterface() {
        const { getCurrentChatMessages, isVoiceInterfaceEnabled } = this._webchatReference;
        if (isVoiceInterfaceEnabled) {
            // console.log("getCurrentChatMessages: ", JSON.stringify(getCurrentChatMessages));
            const latestMessages = this._webchatReference.getLatestIncomingMessages(getCurrentChatMessages);
            // console.log("latestMessages: ", JSON.stringify(latestMessages));
            // fire tts
            this._webchatReference.handleAudioTextToSpeech(latestMessages.join(" "));
        }
    }
    onAfterXmppQueryArchive() {
        setTimeout(() => {
            // handle welcome message
            this.handleWelcomeMessage();
            // get main thread
            const mainThreadLocal = this.getMainThread();
            // reset unread messagges
            this._webchatReference.handleFireResetUnreadMessages(mainThreadLocal);
            // console.log("onAfterXmppQueryArchive callback...");
            // TODO: remove when working
            // this.handleAutocompleteSingleMessage(true);
            // this.handleAutocompleteMultipleMessage(true);
            // TODO: remove when working
            // this.handleCarouselMessage(true);
            // const localMessagesTree = this.getLocalMessagesDictionary();
            // console.log("localMessagesTree: ", JSON.stringify(localMessagesTree));
        }, 300);
    }
    onAfterXmppQueryArchivePagination() {
        setTimeout(() => {
            // console.log("onAfterXmppQueryArchivePagination callback...");
            // TODO: remove when working
            // this.handleAutocompleteSingleMessage(true);
            // this.handleAutocompleteMultipleMessage(true);
            // TODO: remove when working
            // this.handleCarouselMessage(true);
            // const localMessagesTree = this.getLocalMessagesDictionary();
            // console.log("localMessagesTree: ", JSON.stringify(localMessagesTree));
        }, 300);
    }
    onFireVoiceInterface() {
        setTimeout(() => {
            this.handleFireVoiceInterface();
        }, 300);
    }
    onAfterTranscribingSpeech(params) {
        if (params) {
            const { isVoiceInterfaceEnabled } = this._webchatReference;
            if (isVoiceInterfaceEnabled) {
                const { text } = params;
                console.log("onAfterTranscribingSpeech: ", params);
                this.handleSpeechToTextMessage(text);
            }
        }
    }
    onAttachmentError(error) {
        const errorsMap = new Map([
            ["1", strings.attachmentsErrorNotFound],
            ["2", strings.attachmentsErrorExtensionNotAllowed],
            ["3", strings.attachmentsErrorFileTooBig],
            ["1001", strings.attachmentsErrorNotFound],
            ["1002", strings.attachmentsErrorExtensionNotAllowed],
            ["1003", strings.attachmentsErrorFileTooBig],
            ["1101", strings.attachmentsErrorInvalidConversation]
        ]);
        const defaultMsg = strings.attachmentsErrorDefault;
        return error.code ? errorsMap.get("" + error.code) || defaultMsg : defaultMsg;
    }
    renderAsset(imagePath) {
        // @ts-ignore
        return require(`@/assets/${THEME_PATH}/images/${imagePath}`);
    }
    getLocalMessagesDictionary() {
        return this._slsGet(this._getLocalMessageTree) || {};
    }
    getLocalConversationsArray(fetchFromArchive = false) {
        const conversations = this._slsGet(this._getLocalConversationTree, "is_array");
        const conversationArray = fetchFromArchive
            ? conversations.filter((c) => c.is_archived)
            : conversations;
        return conversationArray.sort((c1, c2) => {
            return (c2.pin_date || 0) - (c1.pin_date || 0);
        });
    }
    async emitCloseChatEvent() {
        const code = noticeCodes[4];
        const message = {
            type: "chat_notice",
            action: "chat_notice",
            payload: { code }
        };
        this._webchatReference.sendXMPPMessage(message);
        this._webchatReference.storeMessage(message);
        this.showCloseChatCta = false;
        this.showDialog = false;
        this._webchatReference.updateConversationClass({
            thread: this._webchatReference.currentChat.thread,
            newClass: ConversationType.LocalClosed
        });
        await this._webchatReference.setCurrentChat({});
        setTimeout(async () => {
            await this._webchatReference.$refs.conversationList.renderActiveConversations();
        }, 100);
    }
    emitArchiveChatEvent() {
        // create closing message
        const message = {
            type: "chat_notice",
            action: "chat_notice",
            payload: { code: noticeCodes[4] }
        };
        // send closing message
        this._webchatReference.sendMessageAction(message);
        // update UI
        const conversation = this._webchatReference.currentChat;
        const localConversationsTree = this._slsGet(this._getLocalConversationTree) || [];
        const index = localConversationsTree.findIndex((c) => c.thread === conversation.thread);
        if (index !== -1) {
            localConversationsTree[index].class = ConversationType.Archived;
            localConversationsTree[index].is_archived = true;
            this._slsSet(this._getLocalConversationTree, localConversationsTree);
            this._webchatReference.updateConversationClass({
                thread: conversation.thread,
                newClass: ConversationType.Archived
            });
        }
        this._webchatReference.$refs.conversationList.confirmArchiveAction(conversation);
        this.showCloseChatCta = false;
        this.showDialog = false;
        // go back to conversations list
        this._webchatReference.setCurrentChat({});
    }
    extractTimestampFromMamMessage(message) {
        if (!message || !message.stanza || !message.stanza.rawStanza) {
            return false;
        }
        const parser = new DOMParser();
        const xmlMsg = parser.parseFromString(message.stanza.rawStanza, "text/xml");
        let parsedXMLStanza = document.createElement("div");
        if (xmlMsg.firstElementChild &&
            xmlMsg.firstElementChild.firstElementChild &&
            xmlMsg.firstElementChild.firstElementChild.firstElementChild &&
            xmlMsg.firstElementChild.firstElementChild.firstElementChild.firstElementChild) {
            parsedXMLStanza = xmlMsg.firstElementChild.firstElementChild.firstElementChild.firstElementChild;
        }
        return parsedXMLStanza.getAttribute("stamp");
    }
    handleShowIsTypingBubble(toggle = false) {
        if (typeof this.isTypingBubbleTimeoutId === "number" && !toggle) {
            clearTimeout(this.isTypingBubbleTimeoutId);
            this.isTypingBubbleTimeoutId = undefined;
            this._webchatReference.showIsTypingBubble = false;
            this.showIsTypingBubble = false;
            // enable chat input
            // this._webchatReference.toggleUserInputArea(true);
            this._webchatReference.toggleUserInputStatus(true);
        }
        if (toggle) {
            this._webchatReference.showIsTypingBubble = true;
            this.showIsTypingBubble = true;
            // disable chat input
            // this._webchatReference.toggleUserInputArea(false);
            this._webchatReference.toggleUserInputStatus(false);
            const isTypingTimeout = this.isTypingBubbleTimeoutSecs * 1000;
            this.isTypingBubbleTimeoutId = setTimeout(() => {
                this._webchatReference.showIsTypingBubble = false;
                this.showIsTypingBubble = false;
                // enable chat input
                // this._webchatReference.toggleUserInputArea(true);
                this._webchatReference.toggleUserInputStatus(true);
            }, isTypingTimeout);
        }
    }
};
__decorate([
    Action("setXmppCreds")
], App.prototype, "setXmppCreds", void 0);
__decorate([
    Getter("xmppCreds")
], App.prototype, "xmppCreds", void 0);
App = __decorate([
    Component({
        components: {
            WfpChat,
            CallToAction
        }
    })
], App);
export default App;
